// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { describeRollupLine, rollupProgramInvoice, type RooftopRollup, type RooftopRollupLine } from '@hopdrive/sdk-ar';

import { GetInvoiceDetailsChargesQuery } from '../queries/getInvoiceDetailsCharges.query.generated';

import { getMonetaryValue } from '@services/formatService';
import { buildAmountObjectFromAggregates, getProductCharges } from '@features/invoices/services/calcServiceCharges';

import { css } from '@emotion/css';
import {
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';

import { RooftopExpansion } from '@hopdrive/feature-invoice-display';

import InvoiceDetailsProductsCharges from '@features/invoices/components/InvoiceDetailsProductsCharges';
import InvoiceDetailsTableCharges from '@features/invoices/components/InvoiceDetailsTableCharges';

// TYPES ---------------------------------------------------------------- //

type Invoice = NonNullable<GetInvoiceDetailsChargesQuery['arinvoices_by_pk']>;
type Argroup = Invoice['argroups'][number];
type Rooftop = RooftopRollup<Argroup['archarges'][number], Argroup>;
type Line = RooftopRollupLine<Argroup['archarges'][number]>;

type InvoiceRooftopSectionProps = {
  invoice?: GetInvoiceDetailsChargesQuery['arinvoices_by_pk'];
  // Handed through to the move table untouched, so a partial amount is enough.
  amount?: Partial<ReturnType<typeof buildAmountObjectFromAggregates>>;
  archargeIds?: number[];
  // The modals own the shape of their input; this section only hands it through.
  handleDisputeModalOpen?: (input?: unknown) => void;
  handlePayNowModalOpen?: (input?: unknown) => void;
};

const rooftopKey = (rooftop: Rooftop): string => String(rooftop.customerId ?? rooftop.customerName);

// COMPONENT ---------------------------------------------------------------- //

/**
 * The payer of a program is billed for work done at many dealer rooftops, so
 * the invoice opens on the same lines the PDF and QuickBooks carry: one row per
 * program-wide fee, and one row per rooftop carrying its move cost. A rooftop
 * expands to the move table every other invoice uses, scoped to that rooftop,
 * so each move can still be opened and disputed.
 */
const InvoiceRooftopSection = ({
  invoice,
  amount,
  archargeIds,
  handleDisputeModalOpen,
  handlePayNowModalOpen,
}: InvoiceRooftopSectionProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  const argroups: Argroup[] = invoice?.argroups || [];
  const rollup = React.useMemo(() => rollupProgramInvoice(argroups), [argroups]);
  const { condensed, rooftops } = rollup;
  const [expandedKey, setExpandedKey] = React.useState<string | null>(null);

  if (!invoice || (condensed.length === 0 && rooftops.length === 0)) return null;

  const moveCount = rooftops.reduce((sum, rooftop) => sum + rooftop.moveCount, 0);
  const chargeCount =
    rooftops.reduce((sum, rooftop) => sum + rooftop.chargeCount, 0) + condensed.reduce((sum, line) => sum + line.count, 0);
  const unitAmount = (line: Line) => (line.charges[0] ? Number(line.charges[0].amount || 0) : 0);

  return (
    <div className={cls.section}>
      <Paper variant='custom'>
        <div className={cls.header}>
          <div>
            <Typography variant='h5' sx={{ lineHeight: 1, fontWeight: 600 }}>
              Program Charges
            </Typography>
            <Typography sx={clsx.subtitle}>
              Fees rolled up across the program, move cost rolled up per dealer. Expand a rooftop to see each move.
            </Typography>
          </div>
          <Typography sx={clsx.subtitle}>
            {rooftops.length} rooftop{rooftops.length !== 1 ? 's' : ''} · {moveCount} move{moveCount !== 1 ? 's' : ''} ·{' '}
            {chargeCount} charge{chargeCount !== 1 ? 's' : ''}
          </Typography>
        </div>

        <TableContainer>
          <Table size='small' aria-label='rooftop-table'>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox' />
                <TableCell>
                  <Typography sx={clsx.headLabelText}>Line</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={clsx.headLabelText}>Charges</Typography>
                </TableCell>
                <TableCell align='right'>
                  <Typography sx={clsx.headLabelText}>Total</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {condensed.map(line => (
                <TableRow key={`${line.type}:${line.accountingKey ?? ''}`}>
                  <TableCell padding='checkbox' />
                  <TableCell>
                    <Typography sx={clsx.rooftopName}>{line.label}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={clsx.rowText}>
                      {line.count} charge{line.count !== 1 ? 's' : ''} at {getMonetaryValue(unitAmount(line), true)} each
                    </Typography>
                    {line.credit > 0 && (
                      <Typography sx={clsx.subtitle}>Credits {getMonetaryValue(line.credit, true)}</Typography>
                    )}
                  </TableCell>
                  <TableCell align='right'>
                    <Typography sx={clsx.rowTotal}>{getMonetaryValue(line.amount - line.credit, true)}</Typography>
                  </TableCell>
                </TableRow>
              ))}
              {rooftops.map(rooftop => {
                const key = rooftopKey(rooftop);
                const expanded = expandedKey === key;
                const rooftopChargeIds = new Set(rooftop.charges.map(charge => charge.id));
                const products = getProductCharges(rooftop.argroups).filter(charge => rooftopChargeIds.has(charge.id));
                const summary = rooftop.lines
                  .map(line => `${describeRollupLine(line)} ${getMonetaryValue(line.amount, true)}`)
                  .join(' · ');

                return (
                  <React.Fragment key={key}>
                    <TableRow
                      hover
                      className={cls.rooftopRow}
                      onClick={() => setExpandedKey(expanded ? null : key)}
                      aria-expanded={expanded}
                    >
                      <TableCell padding='checkbox' style={{ borderBottom: expanded ? 'none' : undefined }}>
                        <IconButton size='small' aria-label={expanded ? 'collapse rooftop' : 'expand rooftop'}>
                          {expanded ? <BiChevronUp /> : <BiChevronDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell style={{ borderBottom: expanded ? 'none' : undefined }}>
                        <Typography sx={clsx.rooftopName}>{rooftop.customerName}</Typography>
                        <Typography sx={clsx.subtitle}>
                          {rooftop.moveCount} move{rooftop.moveCount !== 1 ? 's' : ''} · {rooftop.chargeCount} charge
                          {rooftop.chargeCount !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell style={{ borderBottom: expanded ? 'none' : undefined }}>
                        <Typography sx={clsx.rowText}>{summary}</Typography>
                        {rooftop.credit > 0 && (
                          <Typography sx={clsx.subtitle}>Credits {getMonetaryValue(rooftop.credit, true)}</Typography>
                        )}
                      </TableCell>
                      <TableCell align='right' style={{ borderBottom: expanded ? 'none' : undefined }}>
                        <Typography sx={clsx.rowTotal}>{getMonetaryValue(rooftop.total - rooftop.credit, true)}</Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={4} style={{ padding: 0, borderBottom: expanded ? undefined : 'none' }}>
                        <Collapse in={expanded} timeout='auto' unmountOnExit>
                          <RooftopExpansion>
                            {products.length > 0 && <InvoiceDetailsProductsCharges products={products} />}
                            <InvoiceDetailsTableCharges
                              invoice={{ ...invoice, argroups: rooftop.argroups }}
                              amount={amount}
                              archargeIds={archargeIds}
                              handlePayNowModalOpen={handlePayNowModalOpen}
                              handleDisputeModalOpen={handleDisputeModalOpen}
                              embedded
                            />
                          </RooftopExpansion>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (_theme?: Theme) => ({
  section: css`
    position: relative;
  `,
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
  `,
  rooftopRow: css`
    cursor: pointer;
  `,
});

const useSxStyles = (theme: Theme) => ({
  headLabelText: {
    fontSize: '12px',
    fontWeight: 600,
  },
  rooftopName: {
    fontSize: '14px',
    fontWeight: 500,
  },
  rowText: {
    fontSize: '14px',
    fontWeight: 400,
  },
  rowTotal: {
    fontSize: '14px',
    fontWeight: 600,
  },
  subtitle: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
});

// EXPORT ---------------------------------------------------------------- //

export default InvoiceRooftopSection;
